import classNames from 'classnames'
import React from 'react'
import { Link, LinkProps, To } from 'react-router-dom'

import { getElitePageLink, getNavigationLinkUrl } from '../../../../util'
import { QuoteContext } from '../../QuoteContext'
import { QuoteQuery, QuoteSubview, QuoteView } from '../../types'
import { QuoteLinksContext, QuoteLinksContextType } from './QuoteLinksWrapper'

const isPremium = FinvizSettings.hasUserPremium

interface SharedNavigationLinkProps {
  /**
   * Mark as only available to elite users. When se to `true`, free users will be
   * redirected to elite landing when clicking the link
   */
  isEliteOnly?: boolean

  /**
   * Used together with isEliteOnly to set the correct utm campaign param
   */
  utmCampaign?: string
}

interface NavigationLinkProps extends SharedNavigationLinkProps, Omit<LinkProps, 'to'> {
  /**
   * Keys to preserve from the current URL query. Runs first when manipulating the URL
   *
   * @default [Ticker, Type, Period, DateRange]
   */
  preserveQueryKeys?: Array<QuoteQuery & string>

  /**
   * Keys to omit from the current URL query. Runs after `preserveQueryKeys`
   */
  removeQueryKeys?: Array<QuoteQuery & string>

  /**
   * Additional values to set to the URL query after preserving/removing
   *
   * @default { Type: props.view }
   */
  additionalQueryValues?: Partial<Record<QuoteQuery, string | number>>

  /**
   * The view the component links to
   */
  view: QuoteView | QuoteSubview

  getLink?: never
}

interface CustomNavigationLinkProps extends SharedNavigationLinkProps, Omit<LinkProps, 'to'> {
  view?: never
  preserveQueryKeys?: never
  removeQueryKeys?: never
  additionalQueryValues?: never

  /**
   * Custom function to get the link
   */
  getLink: (context: QuoteLinksContextType) => To
}

export function NavigationLink({
  isEliteOnly,
  utmCampaign,
  view,
  getLink,
  reloadDocument = true,
  preserveQueryKeys = [
    QuoteQuery.Ticker,
    QuoteQuery.Type,
    QuoteQuery.Period,
    QuoteQuery.DateRange,
    QuoteQuery.BackToScreener,
  ],
  removeQueryKeys,
  additionalQueryValues,
  ...props
}: React.PropsWithChildren<NavigationLinkProps | CustomNavigationLinkProps>) {
  const quoteContext = React.useContext(QuoteContext)
  const linkContext = React.useContext(QuoteLinksContext)

  const linkUrl = React.useMemo(() => {
    if (isEliteOnly && !isPremium) return getElitePageLink(utmCampaign)
    if (typeof getLink === 'function') return getLink(linkContext)

    return getNavigationLinkUrl(
      linkContext.location,
      preserveQueryKeys,
      removeQueryKeys,
      additionalQueryValues ?? { [QuoteQuery.Type]: view }
    ).toString()
  }, [isEliteOnly, utmCampaign, getLink, linkContext, preserveQueryKeys, removeQueryKeys, additionalQueryValues, view])

  return (
    <Link
      {...props}
      to={linkUrl}
      reloadDocument={(isEliteOnly && !isPremium) || reloadDocument}
      className={classNames(props.className, 'tab-link', {
        'font-semibold': quoteContext.view === view || quoteContext.subview === view,
      })}
    >
      {props.children}
    </Link>
  )
}
